import React from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, PanelLeftClose, PanelLeft, Lock, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useNavigate } from 'react-router-dom';

/**
 * SidebarNav — renders the navigation items for both mobile and desktop sidebars.
 * Extracted from AppShell.js for maintainability.
 */

// Phase A: small "Preparing" badge used while ICP is still being built post-onboarding
function PreparingBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-wide bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30"
      title="AI is still preparing — this section unlocks once your ICP is ready"
      data-testid="nav-preparing-badge"
    >
      <Sparkles className="h-2.5 w-2.5 animate-pulse" />
      Preparing
    </span>
  );
}

export function MobileSidebarNav({ items, location, expandedMenus, activeMenu, onToggleMenu, onSetActiveMenu, onClose }) {
  return (
    <nav className="p-4 space-y-1" data-testid="mobile-sidebar-nav">
      {items.map((item) => {
        if (item.submenu) {
          const isExpanded = expandedMenus[item.name];
          const isMenuActive = activeMenu === item.name;
          return (
            <div key={item.name}>
              <button
                onClick={() => onToggleMenu(item.name)}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                  isMenuActive
                    ? 'bg-primary/10 text-primary font-semibold border-l-4 border-primary ml-0 pl-2'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground font-medium border-l-4 border-transparent'
                }`}
                data-testid={`nav-toggle-${item.name.toLowerCase()}`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className={`h-5 w-5 ${isMenuActive ? 'text-primary' : ''}`} />
                  {item.name}
                  {item.preparing && <PreparingBadge />}
                </div>
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              {isExpanded && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-border pl-2">
                  {item.submenu.map((subItem) => {
                    const isActive = location.pathname === subItem.href;
                    return (
                      <React.Fragment key={subItem.name}>
                        {subItem.separator && (
                          <div className="pt-2 pb-1 px-3">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{subItem.separator}</span>
                          </div>
                        )}
                        {subItem.locked ? (
                          <Link to="/billing"
                            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground/50 hover:bg-muted/50 transition-all duration-200"
                            data-testid={`nav-link-locked-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <subItem.icon className="h-4 w-4 opacity-40" />
                            <span className="flex-1 opacity-60">{subItem.name}</span>
                            <Lock className="h-3.5 w-3.5 text-amber-500" />
                          </Link>
                        ) : (
                          <Link to={subItem.href} onClick={onClose}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                              isActive ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
                                : 'text-muted-foreground hover:bg-secondary hover:text-foreground font-medium'
                            }`}
                            data-testid={`nav-link-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <subItem.icon className="h-4 w-4" />
                            <span className="flex-1">{subItem.name}</span>
                            {subItem.badge && (
                              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white animate-pulse" data-testid={`nav-badge-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}>
                                {subItem.badge > 99 ? '99+' : subItem.badge}
                              </span>
                            )}
                          </Link>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }
        const isMenuActive = activeMenu === item.name;
        return (
          <Link key={item.name} to={item.href}
            onClick={() => { onSetActiveMenu(item.name); onClose(); }}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
              isMenuActive
                ? 'bg-primary/10 text-primary font-semibold border-l-4 border-primary ml-0 pl-2'
                : 'text-muted-foreground hover:bg-secondary hover:text-foreground font-medium border-l-4 border-transparent'
            }`}
            data-testid={`nav-link-${item.name.toLowerCase()}`}
          >
            <item.icon className={`h-5 w-5 ${isMenuActive ? 'text-primary' : ''}`} />{item.name}
            {item.preparing && <PreparingBadge />}
          </Link>
        );
      })}
    </nav>
  );
}

export function DesktopSidebarNav({ items, location, expandedMenus, activeMenu, flyoutPos, collapsed, onToggleMenu, onSetActiveMenu, onCloseMenus }) {
  return (
    <nav className={`flex-1 ${collapsed ? 'p-2' : 'px-3 py-4'} space-y-0.5`}>
      {items.map((item) => {
        if (item.submenu) {
          const isExpanded = expandedMenus[item.name];
          const isMenuActive = activeMenu === item.name;

          const renderFlyout = () => {
            if (!isExpanded) return null;
            return createPortal(
              <>
                <div className="fixed inset-0 z-[9998]" onClick={onCloseMenus} />
                <div
                  className="fixed z-[9999] w-52 bg-card border border-border rounded-lg shadow-lg py-3 animate-in fade-in slide-in-from-left-2 duration-150 overflow-y-auto"
                  style={{ top: flyoutPos.top, left: flyoutPos.left, maxHeight: `calc(100vh - ${flyoutPos.top}px - 16px)` }}
                  data-testid={`flyout-${item.name.toLowerCase()}`}
                >
                  <p className="px-4 pb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{item.name}</p>
                  {item.submenu.map((subItem) => {
                    const isActive = location.pathname === subItem.href;
                    return (
                      <React.Fragment key={subItem.name}>
                        {subItem.separator && (
                          <div className="px-4 pt-3 pb-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">{subItem.separator}</span>
                          </div>
                        )}
                        {subItem.locked ? (
                          <Link to="/billing" onClick={onCloseMenus}
                            className="flex items-center gap-2 mx-2 px-3 py-2 rounded-md text-sm text-muted-foreground/50 hover:bg-amber-500/5 transition-all duration-150"
                            data-testid={`nav-link-locked-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                            title={`Upgrade to ${subItem.requiredPlan} plan`}
                          >
                            <span className="flex-1 opacity-60">{subItem.name}</span>
                            <Lock className="h-3.5 w-3.5 text-amber-500" />
                          </Link>
                        ) : (
                          <Link to={subItem.href} onClick={onCloseMenus}
                            className={`flex items-center gap-2 mx-2 px-3 py-2 rounded-md text-sm transition-all duration-150 ${
                              isActive ? 'bg-primary text-primary-foreground font-semibold'
                                : 'text-foreground hover:bg-primary/10 hover:text-primary font-medium'
                            }`}
                            data-testid={`nav-link-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <span className="flex-1">{subItem.name}</span>
                            {subItem.badge && (
                              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white animate-pulse" data-testid={`nav-badge-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}>
                                {subItem.badge > 99 ? '99+' : subItem.badge}
                              </span>
                            )}
                          </Link>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </>,
              document.body
            );
          };

          const hasBadge = item.submenu.some(s => s.badge);
          const totalBadge = item.submenu.reduce((sum, s) => sum + (s.badge || 0), 0);

          return (
            <div key={item.name}>
              {collapsed ? (
                <div className="relative">
                  <button onClick={(e) => onToggleMenu(item.name, e)}
                    className={`w-full flex items-center justify-center p-2 rounded-lg text-sm font-medium transition-all duration-200 relative ${
                      isMenuActive ? 'bg-primary/15 text-primary shadow-sm'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                    }`}
                    data-testid={`nav-toggle-${item.name.toLowerCase()}`}
                  >
                    <item.icon className={`h-5 w-5 ${isMenuActive ? 'text-primary' : ''}`} />
                    {hasBadge && <span className="absolute -right-0.5 -top-0.5 w-3 h-3 bg-red-500 rounded-full border-2 border-background animate-pulse" data-testid={`nav-badge-dot-${item.name.toLowerCase()}`} />}
                    {isMenuActive && !hasBadge && <span className="absolute right-1 top-1 w-2 h-2 bg-primary rounded-full" />}
                  </button>
                  {renderFlyout()}
                </div>
              ) : (
                <div className="relative">
                  <button onClick={(e) => onToggleMenu(item.name, e)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                      isMenuActive
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground font-medium'
                    }`}
                    data-testid={`nav-toggle-${item.name.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className={`h-[18px] w-[18px] shrink-0 ${isMenuActive ? 'text-primary' : ''}`} />
                      <span>{item.name}</span>
                      {item.preparing && <PreparingBadge />}
                    </div>
                    <div className="flex items-center gap-1">
                      {hasBadge && (
                        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white animate-pulse" data-testid={`nav-badge-parent-${item.name.toLowerCase()}`}>
                          {totalBadge > 99 ? '99+' : totalBadge}
                        </span>
                      )}
                      <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                  {renderFlyout()}
                </div>
              )}
            </div>
          );
        }

        const isMenuActive = activeMenu === item.name;

        if (collapsed) {
          return (
            <TooltipProvider key={item.name}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link to={item.href} onClick={() => onSetActiveMenu(item.name)}
                    className={`flex items-center justify-center p-2 rounded-lg text-sm font-medium transition-all duration-200 relative ${
                      isMenuActive ? 'bg-primary/15 text-primary shadow-sm'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                    }`}
                    data-testid={`nav-link-${item.name.toLowerCase()}`}
                  >
                    <item.icon className={`h-5 w-5 ${isMenuActive ? 'text-primary' : ''}`} />
                    {isMenuActive && <span className="absolute right-1 top-1 w-2 h-2 bg-primary rounded-full" />}
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className={isMenuActive ? 'font-semibold' : ''}>{item.name}</p>
                  {isMenuActive && <p className="text-xs text-primary">Active</p>}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        return (
          <Link key={item.name} to={item.href} onClick={() => onSetActiveMenu(item.name)}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
              isMenuActive
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground font-medium'
            }`}
            data-testid={`nav-link-${item.name.toLowerCase()}`}
          >
            <item.icon className={`h-[18px] w-[18px] shrink-0 ${isMenuActive ? 'text-primary' : ''}`} />
            <span>{item.name}</span>
          </Link>
        );
      })}
    </nav>
  );
}

export function SidebarCollapseButton({ collapsed, onToggle }) {
  return (
    <div className="px-3 py-2 border-t border-border">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" onClick={onToggle}
              className={`w-full ${collapsed ? 'justify-center' : 'justify-start gap-2.5'} text-muted-foreground hover:text-foreground`}
              data-testid="sidebar-collapse-button"
            >
              {collapsed ? <PanelLeft className="h-4 w-4" /> : <><PanelLeftClose className="h-4 w-4" /><span className="text-sm">Collapse</span></>}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>{collapsed ? 'Expand sidebar' : 'Collapse sidebar'}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
